import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { toast } from "react-toastify";
import { quotationStatusOptions } from "./statusOptions";
import StatusRemarkModal from "../StatusRemarkModal/StatusRemarkModal";

interface StatusOption {
  value: string;
  label: string;
  color: string;
}

interface QuotationLogStatusDropdownProps {
  currentStatus: string;
  itemId: string;
  onStatusChange: (
    itemId: string,
    newStatus: string,
    remark?: string
  ) => Promise<void>;
  disabled?: boolean;
  requireRemarks?: boolean;
}

const QuotationLogStatusDropdown: React.FC<QuotationLogStatusDropdownProps> = ({
  currentStatus,
  itemId,
  onStatusChange,
  disabled = false,
  requireRemarks = false,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [remark, setRemark] = useState("");
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const open = Boolean(anchorEl);

  // Normalize current status for comparison
  const normalizedCurrentStatus = currentStatus?.trim() || "";

  // Get current status label and color from all quotation statuses
  const getCurrentStatusLabel = () => {
    const statusOption = quotationStatusOptions.find(
      (opt) => opt.value.toLowerCase() === normalizedCurrentStatus.toLowerCase()
    );
    return (
      statusOption?.label ??
      (normalizedCurrentStatus.toUpperCase() || "UNKNOWN")
    );
  };

  const getCurrentStatusColor = () => {
    const statusOption = quotationStatusOptions.find(
      (opt) => opt.value.toLowerCase() === normalizedCurrentStatus.toLowerCase()
    );
    return statusOption?.color ?? "#6c757d";
  };

  const getCurrentStatusBackgroundColor = () => {
    const statusOption = quotationStatusOptions.find(
      (opt) => opt.value.toLowerCase() === normalizedCurrentStatus.toLowerCase()
    );
    return statusOption?.backgroundColor ?? statusOption?.color ?? "#6c757d";
  };

  const getCurrentStatusBorderColor = () => {
    const statusOption = quotationStatusOptions.find(
      (opt) => opt.value.toLowerCase() === normalizedCurrentStatus.toLowerCase()
    );
    return statusOption?.borderColor ?? statusOption?.color ?? "#6c757d";
  };

  const getCurrentStatusTextColor = () => {
    const statusOption = quotationStatusOptions.find(
      (opt) => opt.value.toLowerCase() === normalizedCurrentStatus.toLowerCase()
    );
    return statusOption?.textColor ?? "#000000";
  };

  // Only allow "Sent" and "Lost" to be selectable
  const selectableStatuses = ["Sent", "Lost"];

  // Check if current status allows dropdown interaction
  const isDropdownDisabled =
    disabled ||
    isUpdating ||
    normalizedCurrentStatus.toLowerCase() === "po received";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isDropdownDisabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = async (newStatus: string) => {
    handleClose();

    if (requireRemarks) {
      setPendingStatus(newStatus);
      setShowModal(true);
      return;
    }

    await performStatusUpdate(newStatus);
  };

  const performStatusUpdate = async (
    newStatus: string,
    statusRemark?: string
  ) => {
    setIsUpdating(true);
    try {
      await onStatusChange(itemId, newStatus, statusRemark);
      toast.success(
        `Status updated to ${quotationStatusOptions.find((opt) => opt.value === newStatus)?.label
        }`
      );
      setShowModal(false);
      setRemark("");
      setPendingStatus(null);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleModalSubmit = () => {
    if (pendingStatus) {
      performStatusUpdate(pendingStatus, remark);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setRemark("");
    setPendingStatus(null);
  };

  // Filter status options to only show selectable ones (Sent and Lost)
  const dropdownOptions = quotationStatusOptions.filter((option) =>
    selectableStatuses.includes(option.value)
  );

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: getCurrentStatusBackgroundColor(),
          color: getCurrentStatusTextColor(),
          border: `1px solid ${getCurrentStatusBorderColor()}`,
          borderRadius: "5px",
          padding: "4px 12px",
          fontSize: "12px",
          fontWeight: "600",
          cursor: isDropdownDisabled ? "not-allowed" : "pointer",
          opacity: isDropdownDisabled ? 0.6 : 1,
          transition: "all 0.2s ease",
          "&:hover": {
            transform: isDropdownDisabled ? "none" : "translateY(-1px)",
            boxShadow: isDropdownDisabled
              ? "none"
              : "0 2px 4px rgba(0,0,0,0.15)",
          },
          "&:active": {
            transform: isDropdownDisabled ? "none" : "translateY(0)",
          },
        }}
      >
        <span style={{ whiteSpace: "nowrap" }}>{getCurrentStatusLabel()}</span>
        {!isDropdownDisabled && (
          <KeyboardArrowDownIcon sx={{ fontSize: "16px", marginLeft: "4px" }} />
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            minWidth: "120px",
            boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
            borderRadius: "10px",
          },
        }}
      >
        {dropdownOptions.map((option) => {
          const isCurrentStatus =
            option.value.toLowerCase() ===
            normalizedCurrentStatus.toLowerCase();

          return (
            <MenuItem
              key={option.value}
              onClick={() => handleStatusSelect(option.value)}
              sx={{
                cursor: "pointer",
                backgroundColor: isCurrentStatus ? "#f1f5f9" : "transparent",
                "&:hover": {
                  backgroundColor: `${option.color}1A`,
                },
              }}
            >
              <ListItemIcon>
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: option.color,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  fontSize: "0.85rem",
                  fontWeight: isCurrentStatus ? 600 : 400,
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Remark Modal */}
      <StatusRemarkModal
        open={showModal}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        remark={remark}
        onRemarkChange={setRemark}
        isUpdating={isUpdating}
      />
    </>
  );
};

export default QuotationLogStatusDropdown;
